import { useNavigate } from 'react-router-dom'

import styles from './ModeBar.module.scss'

import type { MeetingAttributionJob } from '@/api/internal'
import Button from '@/components/Button'
import Select from '@/components/Select'
import Tabs from '@/components/Tabs'
import type { ViewerMode } from '@/pages/app/AttributionViewer/mode'

const MODES: ViewerMode[] = ['inspect', 'annotate', 'replay']
const MODE_LABELS: Record<ViewerMode, string> = {
  inspect: 'Post-Meeting',
  annotate: 'Annotate',
  replay: 'Live Transcript',
}

interface ModeBarProps {
  mode: ViewerMode
  onModeChange: (mode: ViewerMode) => void
  jobs?: MeetingAttributionJob[]
  selectedJobId: string | null
  displayedJobId: string | null
  onJobChange: (jobId: string) => void
}

const formatJobDate = (createdAt: string) => {
  const date = new Date(createdAt)
  return Number.isNaN(date.valueOf())
    ? createdAt
    : date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
}

const ModeBar = ({ mode, onModeChange, jobs, selectedJobId, displayedJobId, onJobChange }: ModeBarProps) => {
  const navigate = useNavigate()
  const hasViewableJob = jobs?.some((job) => job.viewable) ?? false
  const jobControl =
    jobs && jobs.length > 1 ? (
      <Select
        id="attribution-job-select"
        ariaLabel="Job ID"
        title={mode === 'annotate' ? 'Annotation uses the displayed job' : undefined}
        value={mode === 'annotate' ? (displayedJobId ?? '') : (selectedJobId ?? '')}
        options={jobs.map((job, index) => {
          const markers = [...(index === 0 ? ['latest'] : []), ...(job.job_id === displayedJobId ? ['displayed'] : [])]
          return {
            value: job.job_id,
            title: job.job_id,
            isDisabled: !job.viewable,
            label: [
              job.job_id.slice(0, 8),
              formatJobDate(job.created_at),
              job.status,
              ...markers.map((marker) => `(${marker})`),
            ].join(' · '),
          }
        })}
        onChange={onJobChange}
        isDisabled={mode === 'annotate'}
        placeholder={hasViewableJob ? undefined : 'No viewable jobs'}
        className={styles.jobSelect}
      />
    ) : null
  return (
    <div className={styles.modeBar}>
      <div className={styles.left}>
        <Button title="← New meeting" type="light" onClick={() => navigate('/app/attribution')} />
        <Tabs
          tabs={MODES.map((value) => MODE_LABELS[value])}
          activeTab={MODES.indexOf(mode)}
          setActiveTab={(index) => onModeChange(MODES[index] ?? 'inspect')}
        />
      </div>
      {mode === 'replay' ? null : jobControl}
    </div>
  )
}

export default ModeBar
